import type { TUniversityPayload } from '@features/universities/services/universities-api';
import { Textfield } from '@src/shared/fields/text-field';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Label } from '@src/design-system/label';
import { ErrorMessage } from 'formik';
import { ChevronDown } from 'lucide-react';

import {
  DialogSub,
  DialogSubContent,
  DialogSubHeader,
  DialogSubTrigger,
} from '@src/design-system/dialog';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';

import { MembersList } from '@src/shared/components/members-list';
import type { TMember } from '@src/shared/types/member';
import { AddSquare, Building } from 'iconsax-react';
import ManageStatusesDialog from '../../university-statuses/manage-university-statuses-dialog';
import universityStatusesApi from '../../../services/university-statuses-api';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { useFormikContext } from 'formik';

const UniversityInformation = () => {
  const { values, setFieldValue } = useFormikContext<TUniversityPayload>();
  const { data: universityStatuses } = universityStatusesApi.useFetch({
    page_size: 100,
  });

  const selectedStatus = universityStatuses?.results?.find(
    (opt) => opt.id === values.status
  );

  return (
    <div className='space-y-4 border border-gray-200 rounded-[12px] p-4'>
      <div className='flex items-center gap-2'>
        <Building
          className='text-gray-900'
          size={24}
        />
        <p className='text-gray-900 font-medium'>University Information</p>
      </div>
      <div className='grid grid-cols-2 gap-4'>
        <div className='col-span-2'>
          <Textfield
            name='name'
            label='University Name'
            placeholder='University Name'
          />
        </div>

        <div>
          <Label>Status</Label>
          <DialogSub>
            <Popover modal>
              <PopoverTrigger
                className={cn(
                  inputVariants(),
                  'w-full cursor-pointer capitalize flex py-3 justify-between items-center font-normal disabled:opacity-70 text-gray-500 text-sm'
                )}
              >
                {selectedStatus?.name ?? 'Select Status'}
                <ChevronDown
                  size={16}
                  className='shrink-0 self-center'
                />
              </PopoverTrigger>
              <PopoverContent
                withPortal={true}
                style={{
                  width: 'var(--radix-popover-trigger-width)',
                }}
                className='p-0 z-50'
              >
                <div className='max-h-[200px] overflow-y-auto p-1'>
                  {universityStatuses?.results?.map((opt) => (
                    <PopoverClose
                      key={opt.id}
                      onClick={() => setFieldValue('status', opt.id)}
                      className={cn(
                        'flex items-center justify-between capitalize w-full text-sm text-gray-800 p-2 hover:bg-primary-50 rounded-md cursor-pointer',
                        values.status === opt.id &&
                          'bg-primary-50 text-primary-900'
                      )}
                    >
                      {opt.name}
                    </PopoverClose>
                  ))}
                </div>
                <DialogSubTrigger
                  value='status-settings'
                  className='flex hover:bg-primary-50 m-2 mt-0 items-center p-2 hover:underline rounded-b-md cursor-pointer gap-2 text-xs text-primary-700'
                >
                  <span className='cursor-pointer underline flex items-center gap-3 text-sm text-primary-700'>
                    <AddSquare size={16} /> Add Status
                  </span>
                </DialogSubTrigger>
              </PopoverContent>
            </Popover>

            <DialogSubContent
              value='status-settings'
              vertical='top'
              horizontal='left'
              className='max-w-[375px] !translate-x-[calc(-100%-10px)]'
            >
              <DialogSubHeader className='px-4 font-bold text-gray-900'>
                Status Settings
              </DialogSubHeader>
              <ManageStatusesDialog />
            </DialogSubContent>
          </DialogSub>
          <ErrorMessage
            name='status'
            component={CustomErrorMessage}
          />
        </div>
        <div>
          <Label tabIndex={1}>Account Manager</Label>
          <Popover modal>
            <PopoverTrigger
              className={cn(
                inputVariants(),
                'w-full cursor-pointer flex py-3 justify-between items-center font-normal disabled:opacity-70 text-gray-500 text-sm'
              )}
            >
              {values.manager_member ? (
                // <span>
                //     {(values.manager_member as TMember)?.first_name} {(values.manager_member as TMember)?.last_name}
                // </span>
                <div className='flex items-center gap-1'>
                  <Avatar className='h-5 w-5 rounded-full'>
                    <AvatarImage
                      src={
                        (values.manager_member as TMember)?.avatar ?? undefined
                      }
                      alt={
                        (values.manager_member as TMember)?.user.first_name +
                        ' ' +
                        (values.manager_member as TMember)?.user.last_name
                      }
                    />
                    <AvatarFallback className='rounded-xs'>
                      {(values.manager_member as TMember)?.user.first_name
                        .slice(0, 1)
                        .toUpperCase() +
                        (values.manager_member as TMember)?.user.last_name
                          .slice(0, 1)
                          .toUpperCase()}
                    </AvatarFallback>
                  </Avatar>

                  <span className='text-sm font-normal text-gray-800'>
                    {(values.manager_member as TMember)?.user.first_name +
                      ' ' +
                      (values.manager_member as TMember)?.user.last_name}
                  </span>
                </div>
              ) : (
                'Account Manager'
              )}

              <ChevronDown
                size={16}
                className='shrink-0 self-center'
              />
            </PopoverTrigger>
            <PopoverContent
              withPortal={false}
              style={{
                width: 'var(--radix-popover-trigger-width)',
              }}
              className='p-0 z-50'
            >
              <MembersList
                props={{
                  infoItemProps: { withCheckbox: true },
                  itemWrapper: ({ children }) => (
                    <PopoverClose asChild>
                      <div className='contents'>{children}</div>
                    </PopoverClose>
                  ),
                }}
                onClickItem={(member) => {
                  setFieldValue('manager_member', member);
                }}
                isItemSelected={(member) => {
                  return (values.manager_member as TMember)?.id === member.id;
                }}
              />
            </PopoverContent>
          </Popover>

          <ErrorMessage
            name='manager_member'
            component={CustomErrorMessage}
          />
        </div>
        <div className='col-span-2'>
          <Textfield
            name='website'
            label='Website'
            placeholder='https://www.example.com'
          />
        </div>
      </div>
    </div>
  );
};

export default UniversityInformation;
